import * as React from 'react';
import { ScrollBox } from '../../studio/ScrollBox/ScrollBox';
import type { InlineLoaderColor } from '../InlineLoader';
import InlineLoader from '../InlineLoader';
import usePrevious from '../../../hooks/usePrevious';
import type { LogEntry } from '../../../types/Log';
import cn from '~modules/cn';
import Styles from './log-list.module.scss';

type Props = {
    className?: string;
    logs: LogEntry[];
    hasPreviousLogs?: boolean;
    isFetchingPrevious?: boolean;
    fetchPreviousLogs?: () => void;
    loaderColor?: InlineLoaderColor;
};

const LogList = (props: Props) => {
    const scrollBox = React.useRef<HTMLDivElement>(null);
    const isScrollBottomRef = React.useRef(false);
    const firstLogIdRef = React.useRef<string | undefined>();
    const firstLogScrollTopDeltaRef = React.useRef<number | undefined>();

    const prevLogs = usePrevious(props.logs);
    const hadPreviousLogs = usePrevious(props.hasPreviousLogs);
    const hadFetchedPreviousLogs = usePrevious(props.isFetchingPrevious);

    const isLoading = props.isFetchingPrevious || !props.logs.length;

    const isScrollbarOnBottom = React.useCallback(() => {
        if (!scrollBox.current) {
            return false;
        }
        return scrollBox.current.scrollTop >= scrollBox.current.scrollHeight - scrollBox.current.offsetHeight;
    }, []);

    const scrollToBottom = React.useCallback(() => {
        scrollBox.current?.scrollTo({ top: scrollBox.current.scrollHeight });
    }, []);

    const resetScroll = React.useCallback(() => {
        firstLogScrollTopDeltaRef.current = undefined;
        firstLogIdRef.current = undefined;
    }, []);

    React.useEffect(() => {
        if (isScrollBottomRef.current && !isScrollbarOnBottom()) {
            scrollToBottom();
        } else if (!prevLogs?.length && props.logs.length) {
            scrollToBottom();
        }
    });

    React.useEffect(() => {
        if (hadPreviousLogs && !props.hasPreviousLogs) {
            resetScroll();
        }

        const el = scrollBox.current;
        if (hadFetchedPreviousLogs && !props.isFetchingPrevious && el && firstLogIdRef.current) {
            // persist the scroll position. In case previous logs are loaded - we need to persist scrolling
            // by decreasing loader size
            // in case user has scroll while logs were loading - persist it's scrolling position
            if (!el.scrollTop) {
                const logIndex = props.logs.findIndex((log) => log.id === firstLogIdRef.current);
                const firstLogEl = el.children[logIndex] as HTMLElement | undefined;
                if (firstLogEl) {
                    el.scrollTop = firstLogEl.offsetTop - (firstLogScrollTopDeltaRef.current ?? 0);
                }
            }
            resetScroll();
        } else if (!hadFetchedPreviousLogs && props.isFetchingPrevious) {
            firstLogScrollTopDeltaRef.current = (scrollBox.current?.children[1] as HTMLElement)?.offsetTop ?? undefined;
        }
    });

    return (
        <ScrollBox
            className={cn(props.className, Styles['log-list'], { [Styles['initial-loading']]: isLoading && !props.logs.length })}
            ref={scrollBox}
            fullSize
            onScroll={() => {
                const el = scrollBox.current;
                if (!el) {
                    return;
                }
                if (el.scrollTop === 0 && !props.isFetchingPrevious && props.hasPreviousLogs && props.logs.length) {
                    firstLogIdRef.current = props.logs[0]!.id;
                    props.fetchPreviousLogs?.();
                } else {
                    isScrollBottomRef.current = isScrollbarOnBottom();
                }
            }}
        >
            {!isLoading && !props.hasPreviousLogs && <div className={Styles['no-logs']}>There are no more entries to load</div>}
            {isLoading && <InlineLoader className={Styles.loader} color={props.loaderColor ?? 'multipass'} />}
            {props.logs.map((line) => (
                <div key={line.id}>
                    {new Date(line.timestamp).toLocaleTimeString()}: <span className={Styles.line} dangerouslySetInnerHTML={{ __html: line.message }} />
                </div>
            ))}
        </ScrollBox>
    );
};

export default LogList;
